import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ID } from "@segment/analytics-next";
import { useLocation } from "react-router-dom";
import { AuthContext } from "./AuthProvider";
import { APP_ROUTES } from "./Router";
import {
  useAnalyticsIdentifyApiService,
  useAnalyticsTrackApiService,
  useAnalyticsPageApiService,
} from "../common/services/api/AnalyticsApiService";
import UnmatchedDocumentsResult from "../features/payslips/services/model/UnmatchedDocumentsResult";
import DocumentImporterConfiguration from "../features/payslips/services/model/DocumentImporterConfiguration";
import { SupportedFeatures } from "../features/payslips/services/model/FeatureConfiguration";
import { payslipJourneyConfiguration } from "../features/payslips/payslipJourneyConfiguration";
import { irp5JourneyConfiguration } from "../features/irp5/irp5JourneyConfiguration";
import { UploadPayslipDocumentResponse } from "../features/payslips/services/request/UploadPayslipDocumentRequest";
import DocumentProcessingJob from "../features/payslips/services/model/DocumentProcessingJob";
import { RosterJourneyConfiguration } from "../features/roster/RosterJourneyConfiguration";
import { TimesheetJourneyConfiguration } from "../features/timesheet/TimesheetJourneyConfiguration";

export interface DocumentEventInterface {
  type: string;
  event: string;
  description: string;
  event_type:
    | "CTA:Click"
    | "PageView"
    | "ModalView"
    | "Search_Bar_Input"
    | "Toggle"
    | "TooltipView"
    | "Snackbar";
  job:
    | UnmatchedDocumentsResult
    | UploadPayslipDocumentResponse
    | DocumentProcessingJob
    | null;
}
interface AnalyticsContextType {
  analyticsInstance: any;
  trackDocumentsEvent: (
    payload: DocumentEventInterface,
    overload?: any
  ) => void;
}

export const AnalyticsContext = React.createContext<AnalyticsContextType>({
  analyticsInstance: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  trackDocumentsEvent: () => {},
});

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

const AnalyticsProvider = ({
  children,
}: AnalyticsProviderProps): JSX.Element | null => {
  const [analyticsInstance, setAnalyticsInstance] = useState<any>(null);
  const [trackDocumentsEvent, setTrackDocumentsEvent] = useState<() => void>(
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => () => {}
  );
  const [identified, setIdentified] = useState(false);
  const [lastIdentifiedUserId, setLastIdentifiedUserId] = useState<
    string | null
  >(null);
  const location = useLocation();
  const auth = useContext(AuthContext);

  // Helper function to get page info from current route
  const getPageInfo = useCallback(() => {
    const currentPath = location.pathname;

    // Find matching route in APP_ROUTES (including nested children)
    const findRoute = (routes: any[]): any => {
      const matchedRoute = routes.find((route) => route.path === currentPath);
      if (matchedRoute) return matchedRoute;

      // Search in children routes recursively
      return routes.reduce((found, route) => {
        if (found) return found;
        if (route.children) {
          return findRoute(route.children);
        }
        return null;
      }, null);
    };

    const matchedRoute = findRoute(APP_ROUTES);

    return {
      name: matchedRoute?.title || `Page: ${currentPath}`,
      properties: {
        path: currentPath,
        url: globalThis.location?.href || currentPath,
        title: matchedRoute?.title || "Unknown Page",
        description: matchedRoute?.description || "",
        hash: location.hash,
        search: location.search,
        timestamp: new Date().toISOString(),
        ...(auth && {
          employer_id: auth.employer?.id,
          employer_name: auth.employer?.company_name,
          user_role: auth.user_role || "unknown",
        }),
      },
    };
  }, [location.pathname, location.hash, location.search, auth]);

  // Use service layer hooks following architecture patterns
  const identifyMutation = useAnalyticsIdentifyApiService();
  const trackMutation = useAnalyticsTrackApiService();
  const pageMutation = useAnalyticsPageApiService();

  useEffect(() => {
    // SECURITY FIX: Use backend proxy for analytics initialization
    // This removes REACT_APP_SEGMENT_WRITE_KEY from client bundle
    // Analytics events are now sent through secure backend endpoints via service layer

    // Create a mock analytics instance that uses service layer
    const proxyAnalyticsInstance = {
      identify: async (userId: ID, traits?: Record<string, any>) => {
        try {
          if (userId != null) {
            await identifyMutation.mutateAsync({
              userId: userId.toString(),
              traits,
            });
          }
        } catch {
          // Silent fail for analytics - don't break the app
        }
      },
      track: async (event: string, properties?: Record<string, any>) => {
        try {
          await trackMutation.mutateAsync({ event, properties });
        } catch {
          // Silent fail for analytics - don't break the app
        }
      },
      page: async (name?: string, properties?: Record<string, any>) => {
        try {
          await pageMutation.mutateAsync({ name, properties });
        } catch {
          // Silent fail for analytics - don't break the app
        }
      },
      reset: () => {
        // No-op for proxy implementation
      },
    };

    setAnalyticsInstance(proxyAnalyticsInstance);
  }, []); // Empty dependency array - create once and reuse

  useEffect(() => {
    const identify = async (): Promise<void> => {
      if (!analyticsInstance || !auth) return;

      // Deduplication: only identify if user has changed
      const currentUserId = auth.id.toString();
      if (lastIdentifiedUserId === currentUserId) return;

      await analyticsInstance.identify(currentUserId as ID, {
        name: `${auth.first_name} ${auth.last_name}`,
        email: auth.email,
      });

      setLastIdentifiedUserId(currentUserId);
      setIdentified(true);
    };
    identify();
  }, [auth, analyticsInstance, lastIdentifiedUserId]);

  useEffect(() => {
    if (!analyticsInstance || !identified) return;

    const pageInfo = getPageInfo();
    analyticsInstance.page(pageInfo.name, pageInfo.properties);
  }, [location.pathname, analyticsInstance, identified, getPageInfo]);

  // Moved useCallback here to ensure it's in the correct order and always executed
  const baseTrackingObject = useCallback(() => {
    if (!analyticsInstance || !auth) return {};
    return {
      user: auth.email || auth.mobile_number || auth.username,
      platform: "dashboard",
      employer_id: auth.employer.id,
      employer_name: auth.employer.company_name,
      event_date: new Date().toISOString(),
    };
  }, [
    auth?.email,
    auth?.employer?.id,
    auth?.employer?.company_name,
    analyticsInstance,
  ]);

  const trackDocumentsEventCallback = useCallback(
    (payload: DocumentEventInterface, overload: any | undefined) => {
      if (!analyticsInstance || !auth) return;
      let documentImporterConfiguration: DocumentImporterConfiguration;
      switch (payload.type) {
        case SupportedFeatures.PAYSLIPS:
          documentImporterConfiguration = payslipJourneyConfiguration;
          break;
        case SupportedFeatures.IRP5s:
          documentImporterConfiguration = irp5JourneyConfiguration;
          break;
        case SupportedFeatures.ROSTERS:
          documentImporterConfiguration = RosterJourneyConfiguration;
          break;
        case SupportedFeatures.TIMESHEETS:
          documentImporterConfiguration = TimesheetJourneyConfiguration;
          break;
        default:
          return;
      }

      const documentEvent = {
        ...baseTrackingObject(),
        event_description: payload.description,
        flow: "Documents",
        flow_type: documentImporterConfiguration.document_name_plural,
        type: payload.event_type,
        ...(payload.job && {
          batch_id: payload.job.id,
          batch_name: payload.job.batch_name,
          document_status: "status" in payload.job ? payload.job.status : null,
          documents_failed_count:
            "documents_failed_count" in payload.job
              ? payload.job.documents_failed_count
              : null,
          documents_matched_count:
            "documents_matched_count" in payload.job
              ? payload.job.documents_matched_count
              : null,
          document_match_rate:
            "documents_matched_count" in payload.job
              ? (
                  (payload.job.documents_matched_count /
                    payload.job.documents_total_count) *
                  100
                ).toFixed(2)
              : null,
          notification_sent_status:
            "should_send_notifications" in payload.job
              ? payload.job.should_send_notifications
              : null,
          date_document_sent: payload.job.date_of_distribution,
        }),
        ...overload, // overload is used to replace the default values and add additional values
      };

      analyticsInstance.track(payload.event, documentEvent);
    },
    [
      auth,
      analyticsInstance,
      baseTrackingObject,
      payslipJourneyConfiguration,
      irp5JourneyConfiguration,
    ]
  );

  useEffect(() => {
    setTrackDocumentsEvent(() => trackDocumentsEventCallback);
  }, [trackDocumentsEventCallback]);

  const contextValue = useMemo(
    () => ({
      analyticsInstance,
      trackDocumentsEvent,
    }),
    [analyticsInstance, trackDocumentsEvent]
  );

  if (!analyticsInstance || !trackDocumentsEvent) {
    return null;
  }

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): any => {
  const context = React.useContext(AnalyticsContext);
  if (!context.analyticsInstance) {
    throw new Error(
      "useAnalytics must be used within an AnalyticsProvider component"
    );
  }
  return context.analyticsInstance;
};

export default AnalyticsProvider;
